import { FormProvider, useForm } from "react-hook-form";
import React,{ useState, useRef } from "react";
import {
    Box,
    Button,
    Collapse,
    Divider,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Typography,
    useTheme,
} from "@mui/material";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { i18n } from "@lingui/core";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ExpandLess } from "@mui/icons-material";
import StatsFilterForm from "../forms/StatsFilterForm";
import * as dayjs from "dayjs";

const StatisticsFilterPanel = ({
                                   newLabel,
                                   title,
                                   formModel,
                                   formList,
                                   filterDisabled,
                                   onFilterSubmit,
                                   onFilterReset,
                                   user,
                                   isMobile,
                                   setDataLoaded,
                                   setFilters,
                                   headerExtra,
                               }) => {
    const methods = useForm();
    const theme = useTheme();
    const { _ } = useLingui();

    // stato per expand/collapse
    const [expanded, setExpanded] = useState(formList.map(() => false));

    // array di ref per ogni form
    const submitRefs = useRef(formList.map(() => React.createRef()));

    const init_filter_values = {};

    const handleExpandClick = (number) => {
        setExpanded((old) => old.map((o, i) => (i === number ? !o : o)));
    };

    const getSearchParams = (data) => {
        const subSection = data.SubSection;
        let searchParams = {};
        delete data.SubSection;
        Object.keys(data).forEach((section) => {
            // Check if data[section] is an object before iterating
            if (data[section] && typeof data[section] === 'object' && !Array.isArray(data[section])) {
                Object.entries(data[section]).forEach(([key, value]) => {
                    if (value !== undefined && value !== null && value !== '') {
                        if (key.includes("-To") || key.includes("-From")) {
                            const label = key.split("-");
                            searchParams = {
                                ...searchParams,
                                [`${section}.${label[0]}.${label[1].toLowerCase()}`]: dayjs(
                                    value
                                ).toJSON(),
                            };
                        } else {
                            searchParams = { ...searchParams, [`${section}.${key}.equal`]: value };
                        }
                    }
                });
            }
        });
        return searchParams;
    };

    const handleFilterSubmit = (data) => {
        const searchParams = getSearchParams(data);

        const searchParamsArray = Object.entries(searchParams).map(([key, value]) => ({
            name: key,
            value,
        }));

        if (title !== _(msg`Custom charts`)) {
            if (setDataLoaded) setDataLoaded(false);
            if (setFilters) setFilters(searchParamsArray);
        }
        
        // Chiama onFilterSubmit se è definito, indipendentemente dal title
        if (onFilterSubmit) {
            onFilterSubmit(searchParams);
        }
    };

    const closeAllSections = () => setExpanded(formList.map(() => false));

    const handleClickFilter = () => {
        // chiama requestSubmit di tutti i form montati
        submitRefs.current.forEach((ref) => {
            if (ref.current) ref.current.requestSubmit();
        });
        closeAllSections();
    };

    // Build reset values from all elements in formModel
    const buildResetValues = () => {
        const values = { SubSection: title };
        formList.forEach((formType, formNumber) => {
            if (formModel[formType]?.elements) {
                formModel[formType].elements.forEach((elem) => {
                    if (elem.type === 'datetime' || elem.type === 'date') {
                        // For datetime and date, create From and To fields with null
                        values[`${formList[formNumber]}.${elem.label}-From`] = null;
                        values[`${formList[formNumber]}.${elem.label}-To`] = null;
                    } else if (elem.type === 'select') {
                        // For select, reset to empty string to clear selection
                        values[`${formList[formNumber]}.${elem.label}`] = '';
                    } else {
                        // For other types (text, Latitude, Longitude, etc.), reset to empty string
                        values[`${formList[formNumber]}.${elem.label}`] = '';
                    }
                });
            }
        });
        return values;
    };

    return (
        <Paper
            square
            sx={{
                display: "flex",
                flexDirection: "column",
                mt: 1,
                gap: 2,
                pt: 3,
                pb: 3,
                pl: 2,
                pr: 2,
                minHeight: 350,
            }}
        >
            <FormProvider {...methods} {...user}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={2}
                >
                    <Typography
                        sx={{
                            color: theme.palette.mode === "light" ? theme.palette.newColors.gray : theme.palette.newColors.lightGray,
                            fontWeight: 600,
                        }}
                        variant="h5"
                    >
                        <Trans>{"Filters"}</Trans>
                    </Typography>

                    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" ml="auto">
                        {headerExtra}
                        <Button
                            disabled={filterDisabled}
                            variant="contained"
                            sx={{ background: theme.palette.newColors.cyanGradient }}
                            size="small"
                            onClick={handleClickFilter}
                        >
                            <Trans>Filter</Trans>
                        </Button>

                        <Button
                            variant="contained"
                            size="small"
                            sx={{ background: theme.palette.newColors.cyanGradient }}
                            onClick={() => {
                            const resetValues = buildResetValues();
                            
                            // Reset all form values
                            methods.reset(resetValues, { 
                                keepDefaultValues: false,
                                keepValues: false,
                                keepDirty: false,
                                keepErrors: false,
                                keepTouched: false
                            });
                            
                            // Force reset all select fields to ensure visual update
                            formList.forEach((formType, formNumber) => {
                                if (formModel[formType]?.elements) {
                                    formModel[formType].elements.forEach((elem) => {
                                        if (elem.type === 'select') {
                                            // Force reset select to empty string to clear selection
                                            const fieldName = `${formList[formNumber]}.${elem.label}`;
                                            methods.setValue(fieldName, '', {
                                                shouldValidate: false,
                                                shouldDirty: false,
                                                shouldTouch: false
                                            });
                                        } else if (elem.label === 'Latitude' || elem.label === 'Longitude') {
                                            // Force reset Latitude and Longitude to empty string
                                            const fieldName = `${formList[formNumber]}.${elem.label}`;
                                            methods.setValue(fieldName, '', {
                                                shouldValidate: false,
                                                shouldDirty: false,
                                                shouldTouch: false
                                            });
                                        }
                                    });
                                }
                            });
                            
                            closeAllSections();
                            if (setDataLoaded && setFilters) {
                                setFilters([]);
                                setDataLoaded(false);
                            }
                            if (onFilterReset) {
                                onFilterReset();
                            }
                        }}
                    >
                        <Trans>Reset</Trans>
                    </Button>
                    </Box>
                </Box>

                <Divider flexItem sx={{ mt: -1 }} />

                <List
                    sx={{
                        width: "100%",
                        bgcolor: "background.paper",
                        borderRadius: 2,
                        overflow: "hidden",
                        boxShadow: 1,
                    }}
                >
                    {formList.map((item, formNumber) => (
                        <Box key={item}>
                            <ListItemButton
                                onClick={() => handleExpandClick(formNumber)}
                                sx={{
                                    color: theme.mode === "light" && theme.palette.newColors.gray,
                                    borderBottom:
                                        formNumber !== formList.length - 1 && "1px solid #e0e0e0",
                                    px: 2,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    transition: "all 0.2s ease-in-out",
                                }}
                            >
                                <ListItemText
                                    primary={(() => {
                                        const displayName = formModel[item].displayName
                                        const known = {
                                            'crash': _(msg`Crash`),
                                            'people': _(msg`Person`),
                                            'units': _(msg`Traffic unit`),
                                            'road': _(msg`Road`),
                                            'Crash': _(msg`Crash`),
                                            'Road': _(msg`Road`),
                                            'Traffic unit': _(msg`Traffic unit`),
                                            'Person': _(msg`Person`),
                                        }
                                        return known[displayName] ?? displayName
                                    })()}
                                    primaryTypographyProps={{
                                        fontWeight: expanded[formNumber] ? 600 : 500,
                                        fontSize: 15,
                                    }}
                                />
                                {expanded[formNumber] ? (
                                    <ExpandLess
                                        sx={{ color: theme.mode === "light" && theme.palette.newColors.gray }}
                                    />
                                ) : (
                                    <ExpandMoreIcon
                                        sx={{ color: theme.mode === "light" && theme.palette.newColors.gray }}
                                    />
                                )}
                            </ListItemButton>

                            <Collapse sx={{boxShadow:'-2px 0px 1px rgba(0, 0, 0, 0.2)'}} in={expanded[formNumber]} timeout="auto" unmountOnExit={false}>
                                <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0", backgroundColor: theme.mode === "light" && "#fafafa" }}>
                                    <StatsFilterForm
                                        title={title}
                                        isMobile={isMobile}
                                        formModel={formModel}
                                        submitRef={submitRefs.current[formNumber]}
                                        formList={formList}
                                        form={{
                                            number: formNumber,
                                            type: formList[formNumber],
                                            displayName: formModel[formList[formNumber]].displayName,
                                        }}
                                        handleFilterObjSubmit={handleFilterSubmit}
                                    />
                                </Box>
                            </Collapse>
                        </Box>
                    ))}
                </List>
            </FormProvider>
        </Paper>
    );
};

export default StatisticsFilterPanel;
